import React from "react";
import { onlyText } from "react-children-utilities";

interface MarkdownBlockquoteProps {
    children: React.ReactNode;
}

export default function MarkdownBlockquote({ children }: MarkdownBlockquoteProps) {
    const text = onlyText(children);

    function getTextInFirstBrackets(input: string): { type: string | null; title: string | null; full: string | null } {
        if (!input) return { type: null, title: null, full: null };

        const openingBracketIndex = input.indexOf("[");
        const closingBracketIndex = input.indexOf("]");

        if (openingBracketIndex !== -1 && closingBracketIndex !== -1 && closingBracketIndex > openingBracketIndex) {
            const fullText = input.substring(openingBracketIndex, closingBracketIndex + 1).trim();
            const tagTitleText = input.substring(openingBracketIndex + 1, closingBracketIndex).trim();
            const colonIndex = tagTitleText.indexOf(":");

            if (colonIndex !== -1) {
                const tag = tagTitleText.substring(0, colonIndex).trim();
                const title = tagTitleText.substring(colonIndex + 1).trim();
                return { type: tag, title, full: fullText };
            } else {
                return { type: tagTitleText, title: null, full: fullText };
            }
        }

        return { type: null, title: null, full: null };
    }

    function removeTagFromChildren(children: React.ReactNode): React.ReactNode {
        let x: any = children;

        x[1].props.children[0] = x[1].props.children[0].replace(`${tag.full}`, "");

        return x;
    }

    const tag = getTextInFirstBrackets(text);

    if (tag.type === "NOTE") {
        return (
            <div className="my-4 rounded-lg border border-slate-200 bg-slate-100 px-4 leading-6 dark:border-slate-700 dark:bg-slate-800">
                <div className="mt-4 flex items-center gap-2 font-bold text-slate-600 dark:text-slate-300">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                        <path
                            fillRule="evenodd"
                            d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {tag.title !== null ? tag.title : "NOTE"}
                </div>
                <div className="">{removeTagFromChildren(children)}</div>
            </div>
        );
    } else if (tag.type === "INFO") {
        return (
            <div className="my-4 rounded-lg border border-blue-200 bg-blue-100 px-4 leading-6 dark:border-blue-700 dark:bg-blue-800">
                <div className="mt-4 flex items-center gap-2 font-bold text-blue-900 dark:text-blue-300">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                        <path
                            fillRule="evenodd"
                            d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {tag.title !== null ? tag.title : "INFO"}
                </div>
                <div className="">{removeTagFromChildren(children)}</div>
            </div>
        );
    } else if (tag.type === "WARNING") {
        return (
            <div className="my-4 rounded-lg border border-red-200 bg-red-100 px-4 leading-6 dark:border-red-700 dark:bg-red-800">
                <div className="mt-4 flex items-center gap-2 font-bold text-red-900 dark:text-red-300">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                        <path
                            fillRule="evenodd"
                            d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {tag.title !== null ? tag.title : "WARNING"}
                </div>
                <div className="">{removeTagFromChildren(children)}</div>
            </div>
        );
    } else if (tag.type === "IMPORTANT") {
        return (
            <div className="my-4 rounded-lg border border-orange-200 bg-orange-100 px-4 leading-6 dark:border-orange-700 dark:bg-orange-800">
                <div className="mt-4 flex items-center gap-2 font-bold text-orange-900 dark:text-orange-300">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                        <path
                            fillRule="evenodd"
                            d="M11.484 2.17a.75.75 0 011.032 0 11.209 11.209 0 007.877 3.08.75.75 0 01.722.515 12.74 12.74 0 01.635 3.985c0 5.942-4.064 10.933-9.563 12.348a.749.749 0 01-.374 0C6.314 20.683 2.25 15.692 2.25 9.75c0-1.39.223-2.73.635-3.985a.75.75 0 01.722-.516l.143.001c2.996 0 5.718-1.17 7.734-3.08zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zM12 15a.75.75 0 00-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 00.75-.75v-.008a.75.75 0 00-.75-.75H12z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {tag.title !== null ? tag.title : "IMPORTANT"}
                </div>
                <div className="">{removeTagFromChildren(children)}</div>
            </div>
        );
    } else if (tag.type === "SUCCESS") {
        return (
            <div className="my-4 rounded-lg border border-emerald-200 bg-emerald-100 px-4 leading-6 dark:border-emerald-700 dark:bg-emerald-800">
                <div className="mt-4 flex items-center gap-2 font-bold text-emerald-900 dark:text-emerald-300">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                        <path
                            fillRule="evenodd"
                            d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307zm7.007 6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {tag.title !== null ? tag.title : "SUCCESS"}
                </div>
                <div className="">{removeTagFromChildren(children)}</div>
            </div>
        );
    } else {
        return <blockquote>{children}</blockquote>;
    }
}
